import createClient, { type Middleware } from 'openapi-fetch'
import { apiBaseUrl } from './base-url.js'
import { navigate } from 'svelte-routing'
import type { paths } from './generated.js'
import { getAccessToken, setSession, clearSession } from '../auth/session.js'
import { attemptRefresh, fetchMe } from '../auth/login-flow.js'
import { showError } from '../util/errors.js'

// Endpoints that issue/consume auth tokens themselves - a 401 from one of these is the
// actual auth failure (bad credentials, expired refresh token, etc.), not a stale access
// token that a refresh+retry could fix. A plain substring check on '/auth/' would also
// swallow 401s from unrelated routes that happen to contain that text (e.g. '/authors/').
const AUTH_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/me'] as const

let refreshInFlight: Promise<string | null> | null = null

// openapi-fetch's internal fetch call disturbs (locks) the Request body stream, so by the
// time onResponse runs, request.clone() would throw. Stash the body up-front in onRequest -
// before the primary fetch consumes it - so a possible retry after a 401 has something to send.
const bodyCache = new WeakMap<Request, string | null>()

const authInterceptor: Middleware = {
  async onRequest({ request }) {
    const token = getAccessToken()
    if (token) request.headers.set('authorization', `Bearer ${token}`)
    if (request.method !== 'GET' && request.method !== 'HEAD') {
      bodyCache.set(request, await request.clone().text())
    }
    return request
  },
  async onResponse({ request, response }) {
    if (response.status !== 401 || AUTH_PATHS.some((p) => request.url.endsWith(p))) {
      return response
    }

    if (!refreshInFlight) {
      refreshInFlight = attemptRefresh().finally(() => {
        refreshInFlight = null
      })
    }

    try {
      const newToken = await refreshInFlight

      if (!newToken) {
        showError({ detail: 'refresh_expired' })
        clearSession()
        navigate('/login', { replace: true })
        return response
      }

      const me = await fetchMe(newToken)
      setSession(newToken, me)

      const retryHeaders = new Headers(request.headers)
      retryHeaders.set('authorization', `Bearer ${newToken}`)
      const body =
        request.method === 'GET' || request.method === 'HEAD'
          ? null
          : (bodyCache.get(request) ?? null)
      const retryResponse = await fetch(request.url, {
        method: request.method,
        headers: retryHeaders,
        body,
        credentials: 'include',
      })
      // Clone before handing back: concurrent 401s that coalesced onto one refresh may end up
      // reading from the same underlying Response (e.g. two retries resolving to the same
      // mocked instance in tests) - cloning keeps each caller's body stream independent.
      return retryResponse.clone()
    } catch {
      showError({ detail: 'refresh_expired' })
      clearSession()
      navigate('/login', { replace: true })
      return response
    }
  },
}

export const api = createClient<paths>({
  baseUrl: apiBaseUrl(),
  credentials: 'include',
  // openapi-fetch captures `fetch` once at client-creation time (defaulting to
  // globalThis.fetch). Wrap it so the *current* globalThis.fetch is looked up on every
  // call instead - tests swap globalThis.fetch per-case, and this keeps that working.
  fetch: (...args: Parameters<typeof fetch>) => globalThis.fetch(...args),
})
api.use(authInterceptor)
